import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, getToken, onUnauthorized, setToken } from './api';
import type { Cohort, School, User } from './types';

interface SessionValue {
  user: User | null;
  school: School | null;
  cohorts: Cohort[];
  /** Conditional nav items the server decides (§5.3): E-Assessment(s) once a sitting exists for one of your classes. */
  nav: { sittings: boolean; projects: boolean; pypAssessments?: boolean; campuses?: boolean };
  loading: boolean;
  signIn: (email: string, password: string) => Promise<User>;
  signOut: () => void;
  /** Adopt a token minted elsewhere — signup, invite acceptance, or join-by-code. */
  adopt: (token: string, user: User, school?: School | null) => void;
  refresh: () => Promise<void>;
}

const Ctx = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [school, setSchool] = useState<School | null>(null);
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [nav, setNav] = useState<{ sittings: boolean; projects: boolean; pypAssessments?: boolean; campuses?: boolean }>({ sittings: false, projects: false });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setSchool(null);
      setCohorts([]);
      setLoading(false);
      return;
    }
    try {
      const me = await api.get<{ token?: string; user: User; school: School | null; cohorts: Cohort[]; nav?: { sittings: boolean; projects: boolean; pypAssessments?: boolean; campuses?: boolean } }>('/auth/me');
      /* A.6 / B.19 moved this account to another school or role: the server re-issues the token. */
      if (me.token) setToken(me.token);
      setUser(me.user);
      setSchool(me.school);
      setCohorts(me.cohorts ?? []);
      setNav(me.nav ?? { sittings: false, projects: false });
    } catch {
      // A stale token is indistinguishable from no token as far as the UI is concerned.
      setToken(null);
      setUser(null);
      setSchool(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // A 401 from anywhere in the app drops the session rather than leaving a dead shell up.
  useEffect(
    () =>
      onUnauthorized(() => {
        setUser(null);
        setSchool(null);
      }),
    [],
  );

  const signIn = useCallback(async (email: string, password: string) => {
    const res = await api.post<{ token: string; user: User; school: School }>('/auth/login', { email, password });
    setToken(res.token);
    /*
     * B-1: the shell picks PYP mode from the teacher's classes, which only `/auth/me` returns.
     * Setting the user before they arrive painted the non-PYP nav first; load them together.
     */
    await refresh();
    return res.user;
  }, [refresh]);

  const adopt = useCallback((token: string, nextUser: User, nextSchool?: School | null) => {
    setToken(token);
    setUser(nextUser);
    if (nextSchool !== undefined) setSchool(nextSchool);
    void refresh();
  }, [refresh]);

  const signOut = useCallback(() => {
    setToken(null);
    setUser(null);
    setSchool(null);
    setCohorts([]);
  }, []);

  const value = useMemo(
    () => ({ user, school, cohorts, nav, loading, signIn, signOut, adopt, refresh }),
    [user, school, cohorts, nav, loading, signIn, signOut, adopt, refresh],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useSession must be used inside SessionProvider');
  return ctx;
}

/**
 * The session if there is one, `null` if the caller renders outside the provider.
 *
 * The shell needs this: it is rendered by public screens (sign-up, join) and by tests that
 * mount one screen on its own, and neither should have to stand a provider up just so the
 * sidebar can find a name to put next to Sign out.
 */
export function useOptionalSession(): SessionValue | null {
  return useContext(Ctx);
}

/** Where each role lands after signing in. */
export function homeFor(role: User['role']): string {
  if (role === 'parent') return '/parent';
  if (role === 'student') return '/student';
  /* D.1: the owner opens on the executive overview; the coordinator view is one link away. */
  if (role === 'owner') return '/admin/executive';
  if (role === 'admin') return '/admin';
  return '/teacher';
}

const PROGRAMME_OF: Record<string, string> = { ib_pyp: 'pyp', ib_myp: 'myp', ib_dp: 'dp', ib_cp: 'cp' };

/**
 * The classes a class selector offers: a teacher's own; a coordinator's programmes (spec §2.1);
 * everyone else's school. `/auth/me` lists every class in the school for staff.
 */
export function useMyCohorts(): Cohort[] {
  const { user, cohorts } = useSession();
  return useMemo(() => {
    if (user?.role === 'teacher') return cohorts.filter((c) => c.teacherIds.includes(user.id));
    const mine = user?.role === 'admin' ? (user.programmes ?? []) : [];
    if (!mine.length) return cohorts;
    return cohorts.filter((c) => mine.includes(c.programme ?? PROGRAMME_OF[c.curriculumKey ?? ''] ?? ''));
  }, [user, cohorts]);
}
